import { ChangeDetectorRef } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ChatSource } from '../../models/chat.model';
import { ChatItem } from '../../models/chat-ui.model';
import {
  ChatCsvFlowService,
  ChatCsvSelection,
  ChatCsvSubmitEvent,
} from '../../services/chat/flow/chat-csv-flow.service';
import {
  AnswerSourceDocument,
  ChatAnswerSourcesService,
  ParsedSourceSummary,
} from '../../services/chat/presentation/chat-answer-sources.service';
import { ChatDomEffectsService } from '../../services/chat/presentation/chat-dom-effects.service';
import {
  ChatFeedbackPanelState,
  ChatFeedbackUiService,
} from '../../services/chat/presentation/chat-feedback-ui.service';
import { ChatStorageService } from '../../services/chat/session/chat-storage.service';
import { FrontendLoggerService } from '../../services/frontend-logger.service';
import { formatChatError } from './chat-runtime.models';
import { ChatSourceStatePort } from './chat-runtime.ports';
import { ChatSessionController } from './chat-session.controller';

export class ChatSourceController {
  constructor(
    private readonly state: ChatSourceStatePort,
    private readonly answerSources: ChatAnswerSourcesService,
    private readonly csvFlow: ChatCsvFlowService,
    private readonly feedback: ChatFeedbackUiService,
    private readonly storage: ChatStorageService,
    private readonly dom: ChatDomEffectsService,
    private readonly logger: FrontendLoggerService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly sessions: ChatSessionController
  ) {}

  get selectedSummary(): ParsedSourceSummary | null {
    if (this.state.summaryCacheKey === this.state.selectedSource) {
      return this.state.summaryCacheValue;
    }
    this.state.summaryCacheKey = this.state.selectedSource;
    this.state.summaryCacheValue = this.parseSummary(this.state.selectedSource);
    return this.state.summaryCacheValue;
  }

  select(source: ChatSource): void { this.state.selectedSource = source; }
  parseSummary(source: ChatSource | null | undefined): ParsedSourceSummary | null {
    return this.answerSources.parseSourceSummary(source);
  }
  answerDocuments(message: ChatItem | null | undefined): AnswerSourceDocument[] {
    return this.answerSources.getAnswerSourceDocuments(message);
  }
  downloadDocument(documentId: string, event?: MouseEvent): void {
    this.answerSources.downloadAnswerDocument(documentId, this.state.destroy$, event);
  }
  downloadUrl(source: ChatSource | null): string | null {
    return this.answerSources.getSourceDownloadUrl(source);
  }

  onDownloadClick(event: MouseEvent, source: ChatSource | null): void {
    try {
      event.stopPropagation();
      if (!this.downloadUrl(source)) event.preventDefault();
    } catch {
      try { event.preventDefault(); } catch { }
    }
  }

  onRightClick(event: MouseEvent, source: ChatSource): void {
    try {
      event.preventDefault();
      event.stopPropagation();
    } catch { }
    this.select(source);
    this.state.showDetails = true;
    this.changeDetector.markForCheck();
  }

  trackBy(index: number, item: ChatSource): string | number {
    try { return item?.metadata?.document_id || item?.id || index; } catch { return index; }
  }

  clear(): void { this.state.selectedSource = null; }

  sendFeedback(liked: boolean | null): void {
    this.feedback.saveFeedback(liked, this.state.feedbackComment, this.state.feedbackRating)
      .pipe(takeUntil(this.state.destroy$))
      .subscribe({
        next: () => {
          this.logger.info('Feedback enviado correctamente', 'ChatComponent.sendFeedback');
          this.state.showFeedback = false;
          this.applyFeedback(this.feedback.buildSentState());
        },
        error: (error) => {
          this.logger.error(
            'Error al guardar feedback: ' + formatChatError(error),
            'ChatComponent.sendFeedback'
          );
          this.logger.error(
            `[ChatComponent] Error al guardar retroalimentacion: ${JSON.stringify(error)}`
          );
        },
      });
  }

  applyFeedback(panel: ChatFeedbackPanelState): void {
    this.state.feedbackTargetIndex = panel.feedbackTargetIndex;
    this.state.feedbackLike = panel.feedbackLike;
    this.state.feedbackSent = panel.feedbackSent;
    this.state.feedbackRating = panel.feedbackRating;
    this.state.hoverRating = panel.hoverRating;
    this.state.feedbackComment = panel.feedbackComment;
  }

  toggleFeedback(index: number, like: boolean): void {
    this.applyFeedback(this.feedback.buildToggleState(this.state.feedbackTargetIndex, index, like));
  }

  closeFeedback(): void { this.applyFeedback(this.feedback.buildClosedState()); }

  cancelCsv(): void {
    this.state.showCsvModal = false;
    this.state.messages.push(this.csvFlow.buildCancelMessage());
    this.sessions.scrollToBottom();
  }

  submitCsv(data: ChatCsvSelection): void {
    this.logger.info(
      'Enviando consulta CSV: ' + JSON.stringify(data),
      'ChatComponent.onCsvSubmit'
    );
    this.csvFlow.submitCsvQuery(this.state.pendingQuery, data)
      .pipe(takeUntil(this.state.destroy$))
      .subscribe((event) => this.applyCsvEvent(event));
  }

  applyCsvEvent(event: ChatCsvSubmitEvent): void {
    if (event.kind === 'started') {
      this.state.showCsvModal = false;
      this.state.isLoading = true;
      this.state.error = null;
      this.state.feedbackSent = false;
      this.state.messages.push(...event.messages);
      this.sessions.scrollToBottom();
      return;
    }
    this.state.isLoading = false;
    if (event.kind === 'failed') {
      this.state.messages.push(event.message);
      this.sessions.scrollToBottom();
      return;
    }
    if (event.kind === 'plan-limit') {
      this.sessions.showPlanLimit(event.error);
      return;
    }
    if (event.uiResult.kind === 'error') {
      this.state.error = event.uiResult.error;
      return;
    }
    this.state.messages.push(event.uiResult.message);
    this.sessions.scrollToBottom();
  }

  openContext(source: ChatSource, event?: MouseEvent): void {
    try {
      event?.preventDefault();
      event?.stopPropagation();
    } catch { }
    this.state.contextModalSource = source;
    this.state.confirmContext = false;
    this.state.contextModalVisible = true;
    try {
      const position = this.dom.getClampedPosition(event, 260, 120);
      this.state.contextModalX = position.x;
      this.state.contextModalY = position.y;
    } catch {
      this.state.contextModalX = 100;
      this.state.contextModalY = 100;
    }
    this.changeDetector.markForCheck();
  }

  confirmContextSelection(confirmed: boolean): void {
    const source = this.state.contextModalSource;
    if (!source) {
      this.closeContext();
      return;
    }
    if (confirmed) this.persistContextSource(source);
    this.closeContext();
    this.state.contextModalSource = null;
    this.changeDetector.markForCheck();
  }

  private persistContextSource(source: ChatSource): void {
    const documentId = source.metadata?.document_id || source.id || null;
    const name = String(source.original_filename || source.title || '').replace(/\|/g, ' ');
    if (!documentId) return;
    try {
      this.storage.setLimitedDocument(documentId, name);
    } catch {
      this.logger.warn(
        'No se pudo guardar limitChatDocId en localStorage',
        'ChatComponent.confirmContextSelection'
      );
    }
    this.state.limitedDocValue = `${documentId}|${name}`;
    this.state.limitedDocId = documentId;
    this.state.limitedDocName = name;
  }

  private closeContext(): void {
    this.state.contextModalVisible = false;
    this.state.confirmContext = false;
  }
}
